/* eslint-disable no-unused-vars */
/* eslint-disable no-nested-ternary */
import { useState, useCallback } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  useTheme
} from '@material-ui/core';
import { formationApi } from '../../../api/formationApi';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EventNoteRoundedIcon from '@mui/icons-material/EventNoteRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import Scrollbar from '../../Scrollbar';
import * as moment from 'moment';
import useAuth from '../../../hooks/useAuth';
import toast from 'react-hot-toast';

const applyFilters = (sessions, query, filters) => sessions
  .filter((session) => {
    let matches = true;

    if (query) {
      const properties = ['Status', 'numberSession'];
      let containsQuery = false;

      properties.forEach((property) => {
        if (session[property].toLowerCase().includes(query.toLowerCase())) {
          containsQuery = true;
        }
      });

      if (!containsQuery) {
        matches = false;
      }
    }

    Object.keys(filters).forEach((key) => {
      const value = filters[key];

      if (value && session[key] !== value) {
        matches = false;
      }
    });

    return matches;
  });

const applyPagination = (sessions, page, limit) => sessions
  .slice(page * limit, page * limit + limit);

const getTimeState = (session) => {
  const now = moment.now();
  if (moment(now).isBefore(session.start_date)) {
    return { label: 'Non commencée', color: '#2563eb', bg: 'rgba(59, 130, 246, 0.1)', border: 'rgba(59, 130, 246, 0.25)', Icon: ScheduleRoundedIcon };
  }
  if (moment(now).isBefore(session.end_date)) {
    return { label: 'En cours', color: '#059669', bg: 'rgba(16, 185, 129, 0.1)', border: 'rgba(16, 185, 129, 0.25)', Icon: PlayCircleFilledRoundedIcon, live: true };
  }
  return { label: 'Terminée', color: '#64748b', bg: 'rgba(100, 116, 139, 0.1)', border: 'rgba(100, 116, 139, 0.2)', Icon: CheckCircleRoundedIcon };
};

const SessionListTable = (props) => {
  const { sessions, setSessions, ...other } = props;
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('');
  const [filters, setFilters] = useState({
    hasAcceptedMarketing: null,
    isProspect: null,
    isReturning: null
  });
  const [open, setOpen] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const { user } = useAuth();
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const handleClickOpen = (id) => {
    setCurrentSessionId(id);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const handleDelete = async (id) => {
    try {
      await formationApi.deleteSession(id, localStorage.getItem('accessToken'));
      const tempSessions = [...sessions];
      tempSessions.splice(tempSessions.findIndex((sess) => sess.id === id), 1);
      setSessions(tempSessions);
      toast.success('Session supprimée !');
      handleClose();
    } catch (err) {
      console.error(err);
      toast.error('Il y a eu un souci lors de la suppression !');
    }
  };

  const handlePageChange = (event, newPage) => setPage(newPage);
  const handleLimitChange = (event) => setLimit(parseInt(event.target.value, 10));

  const filteredSessions = applyFilters(sessions, query, filters);
  const paginatedSessions = applyPagination(filteredSessions, page, limit);

  const headCellSx = {
    fontWeight: 800,
    fontSize: '0.68rem',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    color: 'text.secondary',
    borderBottom: '1px solid',
    borderColor: isLight ? 'rgba(226, 232, 240, 0.8)' : 'rgba(255, 255, 255, 0.08)',
    backgroundColor: isLight ? 'rgba(248, 250, 252, 0.9)' : 'rgba(15, 23, 42, 0.4)',
    py: 1.5,
  };
  const bodyCellSx = {
    borderColor: isLight ? 'rgba(226, 232, 240, 0.5)' : 'rgba(255, 255, 255, 0.05)',
    py: 1.5,
  };

  return (
    <Card
      sx={{
        border: '1px solid',
        borderColor: isLight ? 'rgba(226, 232, 240, 0.6)' : 'rgba(255, 255, 255, 0.06)',
        borderRadius: '24px',
        overflow: 'hidden',
        boxShadow: isLight ? '0 12px 40px rgba(15, 23, 42, 0.05)' : '0 12px 40px rgba(0, 0, 0, 0.3)',
        background: isLight ? '#fff' : 'rgba(30, 41, 59, 0.45)',
      }}
      {...other}
    >
      {/* ── Dialog de confirmation ── */}
      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{ style: { borderRadius: '20px', padding: '10px' } }}
      >
        <DialogContent>
          <DialogContentText sx={{ fontWeight: 600, color: 'text.primary' }}>
            Souhaitez-vous vraiment supprimer cette session ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => handleDelete(currentSessionId)}
            sx={{ color: '#ef4444', fontWeight: 700, textTransform: 'none', borderRadius: '10px', '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.05)' } }}
          >
            Supprimer
          </Button>
          <Button
            onClick={handleClose}
            autoFocus
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: '#fff', fontWeight: 700, textTransform: 'none', borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
              '&:hover': { background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)' },
            }}
          >
            Annuler
          </Button>
        </DialogActions>
      </Dialog>

      {filteredSessions.length === 0 ? (
        <Box sx={{ py: 8, textAlign: 'center' }}>
          <Box
            sx={{
              width: 64, height: 64, borderRadius: '20px', mx: 'auto', mb: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: 'rgba(59, 130, 246, 0.08)', color: '#3b82f6',
            }}
          >
            <EventNoteRoundedIcon sx={{ fontSize: '2rem' }} />
          </Box>
          <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.95rem' }}>
            Aucune session
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.82rem', mt: 0.5 }}>
            Cette formation ne contient pas encore de session.
          </Typography>
        </Box>
      ) : (
        <>
          <Scrollbar>
            <Box sx={{ minWidth: 760 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Session</TableCell>
                    <TableCell sx={headCellSx}>État</TableCell>
                    <TableCell sx={headCellSx}>Début</TableCell>
                    <TableCell sx={headCellSx}>Fin</TableCell>
                    <TableCell sx={headCellSx}>Apprenants</TableCell>
                    <TableCell sx={headCellSx} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedSessions.map((session) => {
                    const ts = getTimeState(session);
                    const { Icon } = ts;
                    return (
                      <TableRow
                        hover
                        key={session.id}
                        sx={{
                          transition: 'background-color 0.15s, box-shadow 0.15s',
                          '&:hover': {
                            backgroundColor: isLight ? 'rgba(59, 130, 246, 0.04)' : 'rgba(59, 130, 246, 0.08)',
                            boxShadow: 'inset 3px 0 0 0 #3b82f6',
                          },
                          '& td': bodyCellSx,
                        }}
                      >
                        {/* Session */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box
                              sx={{
                                width: 42, height: 42, borderRadius: '12px', flexShrink: 0,
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                                color: '#fff', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                              }}
                            >
                              <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, opacity: 0.85, lineHeight: 1 }}>N°</Typography>
                              <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, lineHeight: 1.1 }}>{session.number_session}</Typography>
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Link
                                component={RouterLink}
                                to={`/dashboard/session/${session.id}`}
                                sx={{
                                  textDecoration: 'none', fontWeight: 700, fontSize: '0.86rem',
                                  color: 'text.primary', transition: 'color 0.15s',
                                  '&:hover': { color: '#3b82f6' },
                                }}
                              >
                                Session n°{session.number_session}
                              </Link>
                              {session.status && (
                                <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'text.secondary', mt: 0.2 }}>
                                  {session.status}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        {/* État */}
                        <TableCell>
                          <Box
                            sx={{
                              display: 'inline-flex', alignItems: 'center', gap: 0.5,
                              px: 1.1, py: 0.45, borderRadius: '9px',
                              backgroundColor: ts.bg, color: ts.color, border: '1px solid', borderColor: ts.border,
                              fontSize: '0.72rem', fontWeight: 700,
                            }}
                          >
                            <Icon sx={{ fontSize: '0.95rem', ...(ts.live ? { animation: 'sessionPulse 1.6s ease-in-out infinite' } : {}) }} />
                            {ts.label}
                          </Box>
                        </TableCell>

                        {/* Début */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                            <CalendarMonthRoundedIcon sx={{ fontSize: '0.95rem', color: 'text.disabled' }} />
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary' }}>
                              {moment(session.start_date).format('DD/MM/YYYY')}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Fin */}
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                            <CalendarMonthRoundedIcon sx={{ fontSize: '0.95rem', color: 'text.disabled' }} />
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary' }}>
                              {moment(session.end_date).format('DD/MM/YYYY')}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Apprenants */}
                        <TableCell>
                          <Box
                            sx={{
                              display: 'inline-flex', alignItems: 'center', gap: 0.6,
                              px: 1.1, py: 0.45, borderRadius: '9px',
                              backgroundColor: 'rgba(100, 116, 139, 0.07)', border: '1px solid rgba(100, 116, 139, 0.16)',
                            }}
                          >
                            <GroupsRoundedIcon sx={{ fontSize: '1rem', color: '#475569' }} />
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: 'text.primary' }}>
                              {session.nbInscrits}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="center">
                          <Tooltip title="Supprimer" arrow>
                            <span>
                              <IconButton
                                onClick={() => handleClickOpen(session.id)}
                                disabled={user.id_service !== 0}
                                size="small"
                                sx={{
                                  color: 'text.secondary', borderRadius: '10px', transition: 'all 0.18s',
                                  '&:hover': { backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', transform: 'translateY(-1px)' },
                                }}
                              >
                                <DeleteOutlineRoundedIcon sx={{ fontSize: '1.2rem' }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Scrollbar>
          <TablePagination
            component="div"
            count={filteredSessions.length}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleLimitChange}
            page={page}
            rowsPerPage={limit}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{
              borderTop: '1px solid',
              borderColor: isLight ? 'rgba(226, 232, 240, 0.8)' : 'rgba(255, 255, 255, 0.08)',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontWeight: 600, fontSize: '0.8rem' },
            }}
          />
        </>
      )}
      <style>
        {`@keyframes sessionPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }`}
      </style>
    </Card>
  );
};

SessionListTable.propTypes = {
  sessions: PropTypes.array.isRequired,
  setSessions: PropTypes.func.isRequired
};

export default SessionListTable;
